/**
 * Hook for fetching and managing dashboard logs with pagination and auto-refresh
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { LogsResponse, LogEvent, TimeRange } from '../types/dashboard';
import { fetchDashboardLogs } from '../lib/dashboard-api';
import { useAuth } from '../components/Auth';

// Auto-refresh interval in milliseconds
const AUTO_REFRESH_INTERVAL = 5000;

// Maximum number of events to keep in memory
const MAX_EVENTS = 500;

interface UseDashboardLogsResult {
  events: LogEvent[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  totalCount: number;
  loadMore: () => void;
  refetch: () => void;
  isLive: boolean;
  setIsLive: (live: boolean) => void;
  lastUpdated: Date | null;
}

/**
 * Generate a unique key for a log event for deduplication
 * Uses timestamp + logStreamName + first 100 chars of message
 */
function getEventKey(event: LogEvent): string {
  const messagePrefix = event.message.slice(0, 100);
  return `${event.timestamp}|${event.logStreamName}|${messagePrefix}`;
}

/**
 * Hook to fetch dashboard logs for a given log group with pagination
 *
 * @param logGroup - The CloudWatch log group to fetch logs from
 * @param timeRange - Time range to fetch logs for (default: '1d')
 * @param limit - Number of log events to fetch per page (default: 50)
 * @returns Object with log events, loading states, error, and pagination functions
 */
export function useDashboardLogs(
  logGroup: string | null,
  timeRange: TimeRange = '1d',
  limit: number = 50,
): UseDashboardLogsResult {
  const { getAuthHeader, isAuthenticated } = useAuth();
  const [events, setEvents] = useState<LogEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const [isLive, setIsLive] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const newestTimestampRef = useRef<string | null>(null);
  const eventKeysRef = useRef<Set<string>>(new Set());

  const refetch = useCallback(() => {
    setEvents([]);
    setOffset(0);
    eventKeysRef.current = new Set();
    newestTimestampRef.current = null;
    setFetchTrigger((prev) => prev + 1);
  }, []);

  const loadMore = useCallback(() => {
    if (!hasMore || isLoadingMore) return;
    setOffset((prev) => prev + limit);
    setFetchTrigger((prev) => prev + 1);
  }, [hasMore, isLoadingMore, limit]);

  // Reset state when log group or time range changes
  useEffect(() => {
    setEvents([]);
    setOffset(0);
    setHasMore(false);
    setTotalCount(0);
    setError(null);
    eventKeysRef.current = new Set();
    newestTimestampRef.current = null;
    setLastUpdated(null);
  }, [logGroup, timeRange]);

  // Main fetch effect
  useEffect(() => {
    if (!isAuthenticated || !logGroup) {
      setIsLoading(false);
      if (!logGroup) {
        setEvents([]);
        setError(null);
      } else {
        setError('Authentication required');
      }
      return;
    }

    const currentLogGroup = logGroup;
    const abortController = new AbortController();
    const isInitialLoad = offset === 0;

    async function loadLogs() {
      if (isInitialLoad) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      setError(null);

      try {
        const authHeader = getAuthHeader();
        if (!authHeader) {
          throw new Error('No authentication token available');
        }

        const response: LogsResponse = await fetchDashboardLogs(
          currentLogGroup,
          { Authorization: authHeader },
          {
            limit,
            offset: isInitialLoad ? 0 : offset,
            timeRange,
          },
          abortController.signal,
        );

        // Events are already sorted newest-first by the backend
        if (isInitialLoad) {
          // Reset and populate event keys
          eventKeysRef.current = new Set(response.events.map(getEventKey));
          setEvents(response.events);
        } else {
          // Append older logs, deduplicating
          setEvents((prev) => {
            const newEvents = response.events.filter((e) => {
              const key = getEventKey(e);
              if (eventKeysRef.current.has(key)) return false;
              eventKeysRef.current.add(key);
              return true;
            });
            return [...prev, ...newEvents];
          });
        }

        // Track the newest timestamp for polling
        if (response.events.length > 0 && isInitialLoad) {
          newestTimestampRef.current = response.events[0].timestamp;
        }

        setTotalCount(response.totalCount);
        setHasMore(response.hasMore);
        setLastUpdated(new Date());
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        setError(err instanceof Error ? err.message : 'Failed to fetch logs');
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }

    loadLogs();

    return () => {
      abortController.abort();
    };
  }, [
    logGroup,
    isAuthenticated,
    getAuthHeader,
    fetchTrigger,
    limit,
    offset,
    timeRange,
  ]);

  // Auto-refresh polling effect (only fetches new logs)
  useEffect(() => {
    if (!isAuthenticated || !logGroup || !isLive || isLoading) {
      return;
    }

    const currentLogGroup = logGroup;

    const intervalId = setInterval(async () => {
      try {
        const authHeader = getAuthHeader();
        if (!authHeader) return;

        // Fetch recent logs - Logs Insights returns newest first
        const response: LogsResponse = await fetchDashboardLogs(
          currentLogGroup,
          { Authorization: authHeader },
          {
            limit: 50, // Fetch enough to catch any new logs
            timeRange,
          },
        );

        if (response.events.length === 0) return;

        // Use key-based deduplication as the primary filter
        // This is more reliable than timestamp comparison which can have
        // issues with parsing, timezone handling, or ingestion delays
        setEvents((prev) => {
          const uniqueNewEvents = response.events.filter((e) => {
            const key = getEventKey(e);
            if (eventKeysRef.current.has(key)) return false;
            eventKeysRef.current.add(key);
            return true;
          });

          if (uniqueNewEvents.length === 0) return prev;

          // Update newest timestamp reference
          const newestEvent = response.events[0];
          if (
            !newestTimestampRef.current ||
            new Date(newestEvent.timestamp) >
              new Date(newestTimestampRef.current)
          ) {
            newestTimestampRef.current = newestEvent.timestamp;
          }

          // Prepend new events (they're newer) and enforce max limit
          const combined = [...uniqueNewEvents, ...prev];
          if (combined.length > MAX_EVENTS) {
            const removed = combined.slice(MAX_EVENTS);
            removed.forEach((e) => eventKeysRef.current.delete(getEventKey(e)));
            return combined.slice(0, MAX_EVENTS);
          }
          return combined;
        });

        setLastUpdated(new Date());
      } catch {
        // Silently ignore polling errors to avoid UI disruption
      }
    }, AUTO_REFRESH_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [logGroup, isAuthenticated, getAuthHeader, isLive, isLoading, timeRange]);

  return {
    events,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    totalCount,
    loadMore,
    refetch,
    isLive,
    setIsLive,
    lastUpdated,
  };
}
